import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Segmented, Select } from 'antd';
import { BaseOptionType } from 'antd/es/select';
import { toast } from 'react-toastify';

import {
  Direction,
  TransactionAlertsLevels,
  UpdateTransactionAlertRequest,
} from '@/api';
import {
  DirectIndirectInputs,
  Button,
  ModalInfo,
  ModalInfoProps,
  OptionAlert,
  OptionSignal,
  Loader,
} from '@/components';
import { AlertType } from '@/models';
import { RiskKeys } from '@/config/riskSignals';

import { DirectIndirectInputsValue } from '@/components/_inputs/DirectIndirectInputs/DirectIndirectInputs';
import { COMMON_CONTAINER_ID } from '@/config/toasts';

import { nullLevels } from '../../TransactionAlerts.constants';
import { getSortedCategoryOptions } from '../TransactionAlertsTable/TransactionAlertsTable.helpers';
import {
  TransactionAlertsEditValue,
  DirectionOption,
  alertGradeOptions,
  transactionAlertsDefaultValue,
} from './TransactionAlertsEditModal.constants';
import { validateAlertLevels } from './TransactionAlertsEditModal.helpers';
import styles from './TransactionAlertsEditModal.module.sass';

const renderSelectedOptionAlert = (option: BaseOptionType) => {
  return <OptionAlert value={option.value} isSelected />;
};

const renderOptionAlert = (option: BaseOptionType) => {
  return <OptionAlert value={option.value} />;
};

const renderSelectedOptionSignal = (option: BaseOptionType) => {
  return <OptionSignal value={option.value} isSelected />;
};

const renderOptionSignal = (option: BaseOptionType) => {
  return <OptionSignal value={option.value} />;
};

interface TransactionAlertsEditModalProps extends ModalInfoProps {
  isLoading?: boolean;
  value?: TransactionAlertsEditValue;
  data: TransactionAlertsLevels;
  onChange: (value: UpdateTransactionAlertRequest) => void;
}

export const TransactionAlertsEditModal = ({
  isOpen,
  isLoading = false,
  value = transactionAlertsDefaultValue,
  data,
  onClose,
  onChange,
}: TransactionAlertsEditModalProps) => {
  const { t } = useTranslation();

  const [cachedValue, setCachedValue] = useState<TransactionAlertsEditValue>({
    category: value.category,
    direction: value.direction,
    alertGrade: value.alertGrade,
    direct: value.direct,
    indirect: value.indirect,
  });

  const categoryOptions = useMemo(
    () => getSortedCategoryOptions(Object.keys(data)),
    [data],
  );

  const directionOptions: DirectionOption[] = [
    { label: `${t('translation.deposit')} ↓`, value: 'income' },
    { label: `${t('translation.withdrawal')} ↑`, value: 'outcome' },
  ];

  const handleOnChangeCategory = useCallback((val: RiskKeys) => {
    setCachedValue((prev: TransactionAlertsEditValue) => ({
      ...prev,
      category: val,
    }));
  }, []);

  const handleOnChangeAlertGrade = useCallback((val: AlertType) => {
    setCachedValue((prev: TransactionAlertsEditValue) => ({
      ...prev,
      alertGrade: val,
    }));
  }, []);

  const handleOnChangeDirection = useCallback((val: Direction) => {
    setCachedValue((prev: TransactionAlertsEditValue) => ({
      ...prev,
      direction: val,
    }));
  }, []);

  const handleOnChangeMinAmount = useCallback(
    (val: DirectIndirectInputsValue) => {
      setCachedValue((prev: TransactionAlertsEditValue) => ({
        ...prev,
        direct: val.direct ?? null,
        indirect: val.indirect ?? null,
      }));
    },
    [],
  );

  const handleOnChange = useCallback(() => {
    const { category, direction, alertGrade, direct, indirect } = cachedValue;
    if (!category || !direction || !alertGrade) {
      return;
    }

    const currentLevels = data?.[category]?.[direction] || {};

    const directLevels = currentLevels.direct || nullLevels;
    const indirectLevels = currentLevels.indirect || nullLevels;

    const validation = validateAlertLevels({
      alertGrade,
      direct,
      indirect,
      directLevels,
      indirectLevels,
    });

    if (!validation.valid && validation.error) {
      toast.error(validation.error, { containerId: COMMON_CONTAINER_ID });
      return;
    }

    onChange({
      [category]: {
        [direction]: {
          direct: {
            ...(currentLevels.direct || nullLevels),
            [alertGrade]: direct,
          },
          indirect: {
            ...(currentLevels.indirect || nullLevels),
            [alertGrade]: indirect,
          },
        },
      },
    });
  }, [cachedValue, data, onChange]);

  useEffect(() => {
    if (value) {
      setCachedValue({
        category: value.category,
        direction: value.direction,
        alertGrade: value.alertGrade,
        direct: value.direct,
        indirect: value.indirect,
      });
    }
  }, [value]);

  return (
    <ModalInfo
      isOpen={isOpen}
      canClose={!isLoading}
      title={t('translation.configure_alert')}
      actions={
        <div className={styles.actions}>
          <Button
            size="s"
            type="secondary"
            corner="rect"
            minWidth={92}
            onClick={onClose}
          >
            {t('translation.cancel_btn')}
          </Button>

          <Button
            size="s"
            type="primary"
            corner="rect"
            minWidth={92}
            onClick={handleOnChange}
          >
            {t('translation.save')}
          </Button>
        </div>
      }
      className={styles.modal}
      onClose={onClose}
    >
      <div className={styles.fields}>
        <div className={styles.field}>
          <div className={styles.label}>{t('translation.category')}:</div>
          <Select
            value={cachedValue.category}
            options={categoryOptions}
            notFoundContent={t('translation.no_matching_categories_found')}
            popupMatchSelectWidth={false}
            labelRender={renderSelectedOptionSignal}
            optionRender={renderOptionSignal}
            popupClassName={styles.dropdown}
            className={styles.select}
            onChange={handleOnChangeCategory}
            showSearch
          />
        </div>

        <div className={styles.field}>
          <div className={styles.label}>{t('translation.alert_grade')}:</div>
          <Select
            value={cachedValue.alertGrade}
            options={alertGradeOptions}
            popupMatchSelectWidth={false}
            labelRender={renderSelectedOptionAlert}
            optionRender={renderOptionAlert}
            popupClassName={styles.dropdown}
            className={styles.select}
            onChange={handleOnChangeAlertGrade}
          />
        </div>

        <div className={styles.field}>
          <div className={styles.label}>{t('translation.direction')}:</div>
          <Segmented
            value={cachedValue.direction ?? directionOptions[0].value}
            options={directionOptions}
            className={styles.segmented}
            onChange={handleOnChangeDirection}
          />
        </div>

        <div className={styles.fieldColumn}>
          <div className={styles.label}>
            {t('translation.min_amount')}, USD:
          </div>
          <DirectIndirectInputs
            value={{
              direct: cachedValue.direct ?? undefined,
              indirect: cachedValue.indirect ?? undefined,
            }}
            onChange={handleOnChangeMinAmount}
          />
        </div>
      </div>

      {isLoading && (
        <div className={styles.loader}>
          <Loader size="small" />
        </div>
      )}
    </ModalInfo>
  );
};
